import { Select, Selection, SelectItem } from "@nextui-org/react";
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import QuizControls from "../QuizControls";
import { QuizProps } from "../QuizModal";

export interface FIBQuiz {
  type: "fill_in_blanks";
  text: string;
  wordsToFill: {
    id: string;
    text: string[];
    correct: string;
  }[];
  times_in_video: number[];
  maxTries: number;
}

interface FillInBlanksQuizProps extends QuizProps {
  quiz: FIBQuiz;
  submission?: Map<number, string>;
  setSubmission?: (submission: Map<number, string>) => void;
}

const FillInBlanksQuiz = memo((props: FillInBlanksQuizProps) => {
  const [selected, setSelected] = React.useState<Map<number, string>>(
    props.submission || new Map<number, string>()
  );
  const [message, setMessage] = React.useState("");

  const { t } = useTranslation();

  const handleReset = () => {
    setSelected(new Map<number, string>());
    props.setSubmitted(false);
    props.setCorrect(false);
    setMessage("");
  };

  const handleSubmission = () => {
    setMessage("");
    if (selected.size === props.quiz.wordsToFill.length) {
      props.setTriesCounter(props.triesCounter + 1);
      props.setSubmitted(true);
      props.setSubmission && props.setSubmission(selected);
      let correct = true;
      selected.forEach((value, index) => {
        const expectedAnswer = props.quiz.wordsToFill[index].correct;
        if (expectedAnswer !== value) {
          correct = false;
        }
      });
      props.setCorrect(correct);
    } else {
      setMessage(t("fib_quiz.all_blanks"));
    }
  };

  const showCorrect = () => {
    setMessage("");
    const newSelected = new Map<number, string>();
    props.quiz.wordsToFill.forEach((word, index) => {
      newSelected.set(index, word.correct);
    });
    setSelected(newSelected);
  };

  return (
    <div className="flex flex-col justify-between">
      <div>
        <span>
          {props.quiz.text.split(/(#\d+)/g).map((part, index) => {
            const match = part.match(/#(\d+)/);
            if (match) {
              const num = parseInt(match[1], 10);
              return (
                <Select
                  key={index}
                  aria-label={"select"}
                  placeholder={props.quiz.wordsToFill[num].text[0]}
                  className="w-28 mx-2"
                  selectionMode="single"
                  selectedKeys={selected.get(num) ? [selected.get(num)!] : []}
                  classNames={{
                    trigger: "p-1",
                  }}
                  popoverProps={{
                    className: "w-48",
                  }}
                  onSelectionChange={(keys: Selection) => {
                    const values = (keys as Set<string | number>).values();
                    const key = values.next().value;
                    setSelected(new Map(selected.set(num, key as string)));
                    setMessage("");
                  }}
                >
                  {props.quiz.wordsToFill[num].text.map(
                    (alternative, index) => {
                      return (
                        <SelectItem key={alternative} value={alternative}>
                          {alternative}
                        </SelectItem>
                      );
                    }
                  )}
                </Select>
              );
            } else {
              return part;
            }
          })}
        </span>
        {message && (
          <div className="mt-4 text-danger">
            <p>{message}</p>
          </div>
        )}
      </div>
      <QuizControls
        selectedQuiz={props.selectedQuiz}
        numberOfQuiz={props.numberOfQuiz}
        onClose={props.onClose}
        handleNextQuiz={props.handleNextQuiz}
        handlePreviousQuiz={props.handlePreviousQuiz}
        handleSubmission={handleSubmission}
        handleReset={handleReset}
        showCorrect={showCorrect}
        maxTries={props.quiz.maxTries}
        submitCount={props.triesCounter}
        correct={props.correct}
      />
    </div>
  );
});

export default FillInBlanksQuiz;
